import React, { useState, useEffect, memo } from 'react';
import { Box, Skeleton } from '@mui/material';
import CustomSvgIcon from './CustomSvgIcon';

export type SvgImageName =
  | 'logomark_purple'
  | 'spy_view'
  | 'server'
  | 'login'
  | 'router'
  | 'chat'
  | 'search'
  | 'cloud_privacy'
  | 'alert'
  | 'team_img_1'
  | 'team_img_2'
  | 'team_img_3'
  | 'team_img_4'
  | 'logo_menu'
  | 'contact_email_img'
  | 'contact_location_img'
  | 'contact_phone_img';

interface SvgIconManagerProps {
  name: SvgImageName;
  size?: number;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  showLoading?: boolean;
  fallback?: React.ReactNode;
}

const svgCache = new Map<string, boolean>();

const SvgIconManager = memo(
  ({
    name,
    size = 64,
    className,
    onClick,
    style,
    showLoading = true,
    fallback,
  }: SvgIconManagerProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      if (svgCache.has(name)) {
        setIsLoaded(true);
        return;
      }

      const timer = setTimeout(() => {
        setIsLoaded(true);
        svgCache.set(name, true);
      }, 100);

      return () => clearTimeout(timer);
    }, [name]);

    const handleError = () => {
      setHasError(true);
      setIsLoaded(true);
    };

    if (hasError && fallback) {
      return <>{fallback}</>;
    }

    if (!isLoaded && showLoading) {
      return (
        <Skeleton
          variant='rectangular'
          width={size}
          height={size}
          sx={{ borderRadius: 1 }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CustomSvgIcon
          name={name}
          size={size}
          className={className}
          onClick={onClick}
          style={style}
        />
      </Box>
    );
  }
);

SvgIconManager.displayName = 'SvgIconManager';

export default SvgIconManager;
